import { Helmet } from 'react-helmet';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Container, Divider, Grid } from '@mui/material';
import FormattedTranslation from 'components/Common/FormattedTranslationMui';

import { setSessionStorageItem, removeSessionStorageItem } from 'utils/webStorage';
import { canUseDOM, getTenantUrl } from 'utils/misc';
import { decodeUrl } from 'utils/url';
import { sessionRedirectKey } from 'utils/constants';
import DividerWithText from 'components/Common/DividerWithText';
import Loader from 'components/Loader';
import PageTitle from 'components/PageTitle';
import TermsAndConditions from 'components/Common/TermsAndConditions';
import ForgotPasswordLink from 'components/Login/ForgotPasswordLink';
import SignUpLink from 'components/Login/SignUpLink';
import LinkLogin from 'containers/Login/LinkLogin';
import LoginContainer from 'containers/Login/Login';
import KeepSession from 'containers/Login/KeepSession';
import SingleSignOn from 'containers/SingleSignOn';
import usePushHome from 'hooks/router/usePushHome';
import usePushSignUp from 'hooks/router/usePushSignUp';
import useReferralEmail from 'hooks/useReferralEmail';

const Login = () => {
  const { formatMessage, locale } = useIntl();
  const authenticated = useSelector(state => state.auth.authenticated);
  const redirectTo = useSelector(state => state.auth.redirectTo);
  const loggingIn = useSelector(state => state.auth.loggingIn);
  const emailRef = useReferralEmail();
  const tenantId = useSelector(state => state.tenant.id);
  const tenantUrls = useSelector(state => state.tenant.urls);
  const pushHome = usePushHome();
  const pushSignUp = usePushSignUp({ keepQueries: true });

  const privacyUrl = getTenantUrl(tenantUrls, 'privacy', locale);
  const termsUrl = getTenantUrl(tenantUrls, 'terms', locale);

  useEffect(() => (function cleanUp() {
    if (authenticated) {
      // Destroy the redirect link, it should not be used by any other page.
      removeSessionStorageItem(sessionRedirectKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  useEffect(() => {
    // If there is a redirect link available when loaded, set it to redux store and session storage.
    if (redirectTo && canUseDOM()) {
      // Session storage is used to solve the issue of redirect link being missing when logging in with SSO
      setSessionStorageItem(sessionRedirectKey, decodeUrl(redirectTo));
    }
  }, [redirectTo]);

  useEffect(() => {
    // When authenticated, navigate to the home page (bookings list)
    if (authenticated && !loggingIn) {
      pushHome();
    }
  }, [authenticated, pushHome, loggingIn]);

  useEffect(() => {
    // Navigate to the Sign up form if an email is referred in the URL and there's no user identified with it
    if (emailRef.checked && !emailRef.exists && !authenticated) {
      pushSignUp();
    }
  }, [authenticated, emailRef.checked, emailRef.exists, pushSignUp]);

  const handleSignUpClick = () => {
    pushSignUp();
  };

  return (
    <Container maxWidth="sm">
      <Helmet title={formatMessage({ id: 'page.login.header' })} />
      <PageTitle align="center" size="medium">
        <FormattedTranslation id="page.login.header" />
      </PageTitle>
      <Loader visible={loggingIn || emailRef.checking} />
      <Grid container direction="column" alignItems="stretch" spacing={2}>
        <Grid>
          <KeepSession />
        </Grid>
        <Grid>
          <LinkLogin />
        </Grid>
        <Grid>
          <LoginContainer />
        </Grid>
        <Grid>
          <DividerWithText textId="page.login.oneClickLogin" />
        </Grid>
        <Grid>
          <SingleSignOn />
        </Grid>
        <Grid>
          <SignUpLink onClick={handleSignUpClick} tenantId={tenantId} />
        </Grid>
        <Grid>
          <ForgotPasswordLink />
        </Grid>
        <Grid>
          <Divider />
          <TermsAndConditions privacyUrl={privacyUrl} termsUrl={termsUrl} />
        </Grid>
      </Grid>
    </Container>
  );
};

export default Login;
